import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import { useBuildingOptions } from '../../common/hooks/useBuildingOptions'
import { useEventTypeOptions } from '../../common/hooks/useEventTypeOptions'
import { useTrainerOptions } from '../../common/hooks/useTrainerOptions'
import { useSocialLinksOptions } from '../../common/hooks/useSocialLinksOptions'
import {
  getInitialTimeStart,
  getInitialTimeEnd,
  defaultFrequencyOption,
} from '../../common/helpers'
import { EMAIL_REGEX } from '../../../../helpers/constants'
import { mapClassDtoToFormData } from '../mappers'

const getInitialValues = (timezone, t, advancedTicketingEnabled) => ({
  // Event details
  building: null,
  type: null,
  name: '',
  organizer: null,
  description: '',
  image: null,
  // Date/Time
  place: '',
  timeStart: getInitialTimeStart(timezone),
  timeEnd: getInitialTimeEnd(timezone),
  frequency: defaultFrequencyOption,
  // Tickets
  capacity: 1,
  tickets: [
    {
      name: t('eventManagement:classFormPage.thirdStep.defaultTicketName'),
      price: 0,
      description: t('eventManagement:classFormPage.thirdStep.defaultTicketDescription'),
      ...(advancedTicketingEnabled && { inactive: false }),
    },
  ],
  // Advanced - rsvp
  hideAttendees: false,
  // Advanced - Communication
  automatedRemindersToggle: false,
  remindRsvp: false,
  remindRsvpValues: [''],
  remindAll: false,
  remindAllValues: [''],
  askForPostEventReview: false,
  positiveReview: false,
  socialLinkService: '',
  replyTo: '',
})

const makeValidationSchema = (t) =>
  Yup.object().shape({
    building: Yup.object()
      .nullable()
      .required(t('eventManagement:classFormPage.validation.buildingRequired')),
    type: Yup.object()
      .nullable()
      .required(t('eventManagement:classFormPage.validation.typeRequired')),
    name: Yup.string().required(t('eventManagement:classFormPage.validation.nameRequired')),
    organizer: Yup.object()
      .nullable()
      .required(t('eventManagement:classFormPage.validation.facilitatorRequired')),
    description: Yup.string().required(
      t('eventManagement:classFormPage.validation.descriptionRequired'),
    ),
    image: Yup.object()
      .nullable()
      .required(t('eventManagement:classFormPage.validation.photoRequired')),
    location: Yup.string().required(t('eventManagement:classFormPage.validation.locationRequired')),
    timeStart: Yup.object().required(
      t('eventManagement:classFormPage.validation.dateTimeRequired'),
    ),
    replyTo: Yup.string().when('positiveReview', {
      is: true,
      then: (schema) =>
        schema
          .required(t('eventManagement:classFormPage.validation.replyToRequired'))
          .matches(EMAIL_REGEX, t('eventManagement:classFormPage.validation.invalidEmail')),
    }),
  })

export const useClassFormState = ({ userData, eventData, t }) => {
  const selectedBuilding = useSelector(selectActiveBuilding)
  const selectedBuildingId = selectedBuilding ? selectedBuilding.id : null
  const selectedBuildingTimezone = selectedBuilding?.timezone
  const advancedTicketingEnabled = selectedBuilding?.eventTicketsAvailable

  const { buildingOptions, isLoading: isBuildingOptionsLoading } = useBuildingOptions(userData)
  const { eventTypeOptions, isLoading: isEventTypeOptionsLoading } = useEventTypeOptions(userData)
  const { trainerOptions, isLoading: isTrainerOptionsLoading } = useTrainerOptions(userData)
  const { isLoading: isSocialLinksLoading } = useSocialLinksOptions(selectedBuildingId)

  const { availableOptions: socialLinkOptions } = useSocialLinksOptions(selectedBuildingId, true)

  const initialValues = useMemo(() => {
    return eventData
      ? mapClassDtoToFormData({
          eventData,
          trainerOptions,
          buildingOptions,
          eventTypeOptions,
        })
      : getInitialValues(selectedBuildingTimezone, t, advancedTicketingEnabled)
  }, [eventData, buildingOptions, eventTypeOptions, trainerOptions, t])

  const validationSchema = useMemo(() => makeValidationSchema(t), [t])

  return {
    initialValues,
    validationSchema,
    buildingOptions,
    eventTypeOptions,
    trainerOptions,
    availableOptions: socialLinkOptions,
    areOptionsLoading:
      isBuildingOptionsLoading ||
      isEventTypeOptionsLoading ||
      isTrainerOptionsLoading ||
      isSocialLinksLoading,
  }
}
